// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Section, SectionRail } from './primitives';
import {
  getComplianceOverview,
  updateComplianceSettings,
  upsertCredential,
} from '../../../api/complianceApi';
import LicenseIntake from '../../../components/Compliance/LicenseIntake';
import './compliance.css';

/* Compliance & Credentials — /settings/compliance.
   Which licenses / certifications / insurance the business must hold (derived
   from industry + state), what's entered, what's lapsing, and who's booked on
   work they aren't credentialed for. Modeled on the setup-confirm surface:
   the checklist is CERTAIN (pack-derived), a pre-filled holder is a SUGGESTION,
   and license numbers / expiry dates are TYPED — never guessed.
   Honest framing: empty is the starting state, not an alarm. FLAG mode — the
   schedule is never auto-changed.
   Contract: COMPLIANCE_ENDPOINT_CONTRACT_2026-09-07.md */

const SECTIONS = [
  { id: 'reqs', label: 'Requirements' },
  { id: 'held', label: 'On file' },
  { id: 'risks', label: 'Renewals & risk' },
  { id: 'alerts', label: 'Alerts' },
];

const STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

const SEV_RANK = { critical: 0, high: 1, medium: 2, low: 3 };
const STATUS_CLASS = { active: 'ok', expiring: 'warn', expired: 'danger', pending: 'off' };
const STATUS_LABEL = { active: 'Active', expiring: 'Expiring', expired: 'Expired', pending: 'No expiry entered' };

const fmtDate = (v) => { if (!v) return '—'; try { return new Date(v + (v.length === 10 ? 'T00:00:00' : '')).toLocaleDateString(); } catch { return v; } };
const expiryPhrase = (c) => {
  if (c.days_to_expiry == null) return 'no expiry entered';
  const d = Number(c.days_to_expiry);
  if (d < 0) return `expired ${Math.abs(d)} day${Math.abs(d) === 1 ? '' : 's'} ago`;
  if (d === 0) return 'expires today';
  return `expires in ${d} day${d === 1 ? '' : 's'}`;
};
const mapWriteError = (e) => {
  const code = e?.payload?.code || e?.code;
  if (e?.status === 403) return 'Only an admin or manager can do this.';
  if (code === 'unknown_type') return 'That credential type isn’t recognized.';
  if (code === 'holder_required') return 'Pick which team member holds this license.';
  return e?.payload?.detail || e?.payload?.error || e?.message || 'Couldn’t save.';
};

function Pill({ cls, children }) { return <span className={`sv2-pill ${cls}`}>{children}</span>; }

/* ── Typed entry (the fallback when a document can't be read / intake isn't deployed) ── */
function TypedEntry({ req, tech, existing, onSaved, onCancel }) {
  const schema = req.attributes_schema || {};
  const [form, setForm] = useState({
    number: existing?.number || '',
    issue_date: existing?.issue_date || '',
    expiry_date: existing?.expiry_date || '',
    category: existing?.attributes?.category || '',
    rup: !!existing?.attributes?.rup,
    ceu_earned: existing?.ceu_earned ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.number.trim()) { setErr('Enter the license / policy number — it can’t be inferred.'); return; }
    setBusy(true); setErr(null);
    try {
      const attributes = {};
      if (Array.isArray(schema.category)) attributes.category = form.category;
      if (schema.rup) attributes.rup = !!form.rup;
      const body = {
        type_key: req.type_key,
        number: form.number.trim(),
        issue_date: form.issue_date || undefined,
        expiry_date: form.expiry_date || undefined,
        attributes,
        ceu_earned: form.ceu_earned === '' ? undefined : Number(form.ceu_earned),
        source: 'owner_entered',
      };
      if (req.holder_level === 'employee' && tech?.tech_id != null) body.holder_tech_id = tech.tech_id;
      await upsertCredential(body);
      toast.success(existing ? 'Updated' : 'Saved');
      onSaved();
    } catch (ex) { setErr(mapWriteError(ex)); }
    finally { setBusy(false); }
  };

  return (
    <form className="cmp-entry" onSubmit={submit}>
      {req.holder_level === 'employee' && (
        <div className="cmp-entry-holder">
          <span className="cmp-entry-label">Holder</span>
          <span className="cmp-entry-holdername">{tech?.name || '—'}</span>
          <span className="cmp-entry-hint">suggested from who logs the work — correct it if wrong</span>
        </div>
      )}
      <div className="cmp-entry-grid">
        <label className="cmp-field">
          <span>Number <em>required · you type this</em></span>
          <input type="text" value={form.number} onChange={(e) => set('number', e.target.value)} placeholder="e.g. JF12345" autoFocus />
        </label>
        <label className="cmp-field">
          <span>Expiry date</span>
          <input type="date" value={form.expiry_date} onChange={(e) => set('expiry_date', e.target.value)} />
        </label>
        <label className="cmp-field">
          <span>Issue date <em>optional</em></span>
          <input type="date" value={form.issue_date} onChange={(e) => set('issue_date', e.target.value)} />
        </label>
        {Array.isArray(schema.category) && (
          <label className="cmp-field">
            <span>Category</span>
            <select value={form.category} onChange={(e) => set('category', e.target.value)}>
              <option value="">Select…</option>
              {schema.category.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
        )}
        {req.ceu_required > 0 && (
          <label className="cmp-field">
            <span>CEUs earned <em>of {req.ceu_required}</em></span>
            <input type="number" min="0" value={form.ceu_earned} onChange={(e) => set('ceu_earned', e.target.value)} />
          </label>
        )}
        {schema.rup && (
          <label className="cmp-field cmp-field--check">
            <input type="checkbox" checked={form.rup} onChange={(e) => set('rup', e.target.checked)} />
            <span>RUP endorsement (restricted-use pesticides)</span>
          </label>
        )}
      </div>
      {err && <div className="cmp-note danger">{err}</div>}
      <div className="sv2-actions">
        <button type="submit" className="sv2-btn primary" disabled={busy}>{busy ? 'Saving…' : existing ? 'Update' : 'Save'}</button>
        <button type="button" className="sv2-btn" onClick={onCancel} disabled={busy}>Cancel</button>
      </div>
    </form>
  );
}

/* ── Document-first entry: upload the license PDF → confirm what it read (lane D extracts + stores) ── */
function CredentialEntry(props) {
  return <LicenseIntake {...props} typedFallback={<TypedEntry {...props} />} />;
}

export default function ComplianceTab() {
  const [ov, setOv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [stateDraft, setStateDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [entry, setEntry] = useState(null); // { type_key, tech_id }
  const container = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await getComplianceOverview();
      setOv(data); setLoadError(null);
    } catch (e) {
      setLoadError(e?.status === 403 ? 'You don’t have access to compliance settings.' : 'Couldn’t load compliance.');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const reqBlock = ov?.requirements || {};
  const needsState = !!reqBlock.needs_jurisdiction;
  const reqs = reqBlock.requirements || [];
  const creds = ov?.credentials || [];
  const flags = ov?.flags || [];
  const risks = useMemo(() => [...(ov?.risks || [])].sort((a, b) => (SEV_RANK[a.severity] ?? 9) - (SEV_RANK[b.severity] ?? 9)), [ov]);
  const alertsOn = !!ov?.expiry_alerts_enabled;
  const jurisdiction = reqBlock.jurisdiction;
  const industry = reqBlock.industry;

  const credFor = (type_key, tech_id) =>
    creds.find((c) => c.type_key === type_key && (tech_id == null ? c.is_company : c.holder_tech_id === tech_id));

  const saveState = async () => {
    if (!stateDraft) return;
    setBusy(true);
    try { await updateComplianceSettings({ jurisdiction: stateDraft }); toast.success(`State set to ${stateDraft}`); await load(); }
    catch (e) { toast.error(mapWriteError(e)); }
    finally { setBusy(false); }
  };
  const toggleAlerts = async (next) => {
    setBusy(true);
    try { await updateComplianceSettings({ expiry_alerts_enabled: next }); setOv((o) => ({ ...o, expiry_alerts_enabled: next })); toast.success(next ? 'Expiry alerts on' : 'Expiry alerts off'); }
    catch (e) { toast.error(mapWriteError(e)); }
    finally { setBusy(false); }
  };

  const onSaved = async () => { setEntry(null); await load(); };
  const isOpen = (type_key, tech_id) => entry && entry.type_key === type_key && (entry.tech_id ?? null) === (tech_id ?? null);

  return (
    <div className="sv2-body">
      <SectionRail sections={SECTIONS} containerRef={container} />
      <div className="sv2-col" ref={container}>

        {/* ── Requirements (the checklist) ── */}
        <Section id="reqs" title="Requirements"
          sub={jurisdiction
            ? `What a ${String(industry || '').replace(/_/g, ' ')} business in ${jurisdiction} is required to hold. The list is certain; the numbers and dates are yours to enter.`
            : 'Which licenses, certifications and insurance your business must hold.'}>
          {loading && !ov ? (
            <div className="cmp-muted">Loading…</div>
          ) : loadError ? (
            <div className="cmp-note danger">{loadError}</div>
          ) : needsState ? (
            <div className="sv2-obj cmp-state">
              <div className="sv2-obj-head"><span className="sv2-obj-title">Set your state first</span><Pill cls="info">Needed</Pill></div>
              <div className="cmp-muted">{reqBlock.note || 'We don’t know your state yet — set it and we’ll propose the licenses and permits your business needs.'}</div>
              <div className="sv2-actions">
                <select className="cmp-select" value={stateDraft} onChange={(e) => setStateDraft(e.target.value)}>
                  <option value="">State…</option>
                  {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <button className="sv2-btn primary" disabled={!stateDraft || busy} onClick={saveState}>{busy ? 'Saving…' : 'Save state'}</button>
              </div>
            </div>
          ) : reqs.length === 0 ? (
            <div className="sv2-obj">
              <div className="sv2-obj-head"><span className="sv2-obj-title">No licensing requirements on file</span><Pill cls="off">None</Pill></div>
              <div className="cmp-muted">Nothing is required for your industry and state in our packs — this isn’t an error.</div>
            </div>
          ) : (
            <div className="sv2-obj-list">
              {reqs.map((req) => {
                const isEmp = req.holder_level === 'employee';
                const companyCred = !isEmp ? credFor(req.type_key, null) : null;
                return (
                  <div key={req.type_key} className={`sv2-obj cmp-req ${req.satisfied ? 'is-satisfied' : ''}`}>
                    <div className="sv2-obj-head">
                      <div className="cmp-req-title">
                        <span className="sv2-obj-title">{req.label}</span>
                        <span className="sv2-obj-sub">{req.issuing_body}{req.renewal_period_months ? ` · renews every ${req.renewal_period_months} mo` : ''}{req.ceu_required ? ` · ${req.ceu_required} CEUs` : ''}</span>
                      </div>
                      <Pill cls={req.satisfied ? 'ok' : 'warn'}>{req.satisfied ? 'Have it' : (req.status || 'Missing')}</Pill>
                    </div>

                    {isEmp ? (
                      <div className="cmp-rows">
                        {/* held, per tech */}
                        {creds.filter((c) => c.type_key === req.type_key && !c.is_company).map((c) => {
                          const tech = { tech_id: c.holder_tech_id, name: c.holder_name || (req.missing_for || []).find((t) => t.tech_id === c.holder_tech_id)?.name || `Tech #${c.holder_tech_id}` };
                          return (
                            <div key={`h-${c.id}`} className="cmp-row">
                              <div className="cmp-row-main">
                                <span className="cmp-row-name">{tech.name}</span>
                                <span className="cmp-row-meta">#{c.number} · {expiryPhrase(c)}{c.attributes?.category ? ` · ${c.attributes.category}` : ''}{c.attributes?.rup ? ' · RUP' : ''}</span>
                              </div>
                              <div className="cmp-row-side">
                                <Pill cls={STATUS_CLASS[c.status] || 'off'}>{STATUS_LABEL[c.status] || c.status}</Pill>
                                <button className="sv2-btn sm" onClick={() => setEntry({ type_key: req.type_key, tech_id: c.holder_tech_id })}>Edit</button>
                              </div>
                              {isOpen(req.type_key, c.holder_tech_id) && <CredentialEntry req={req} tech={tech} existing={c} onSaved={onSaved} onCancel={() => setEntry(null)} />}
                            </div>
                          );
                        })}
                        {/* missing, per tech — the "Enter license" rows */}
                        {(req.missing_for || []).map((t) => (
                          <div key={`m-${t.tech_id}`} className="cmp-row cmp-row--missing">
                            <div className="cmp-row-main">
                              <span className="cmp-row-name">{t.name}</span>
                              <span className="cmp-row-meta">nothing on file</span>
                            </div>
                            <div className="cmp-row-side">
                              <button className="sv2-btn primary sm" onClick={() => setEntry({ type_key: req.type_key, tech_id: t.tech_id })}>Enter license</button>
                            </div>
                            {isOpen(req.type_key, t.tech_id) && <CredentialEntry req={req} tech={t} existing={null} onSaved={onSaved} onCancel={() => setEntry(null)} />}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="cmp-rows">
                        <div className={`cmp-row ${companyCred ? '' : 'cmp-row--missing'}`}>
                          <div className="cmp-row-main">
                            <span className="cmp-row-name">Company</span>
                            <span className="cmp-row-meta">{companyCred ? `#${companyCred.number} · ${expiryPhrase(companyCred)}` : 'nothing on file'}</span>
                          </div>
                          <div className="cmp-row-side">
                            {companyCred && <Pill cls={STATUS_CLASS[companyCred.status] || 'off'}>{STATUS_LABEL[companyCred.status] || companyCred.status}</Pill>}
                            <button className={`sv2-btn sm ${companyCred ? '' : 'primary'}`} onClick={() => setEntry({ type_key: req.type_key, tech_id: null })}>{companyCred ? 'Edit' : 'Add it'}</button>
                          </div>
                          {isOpen(req.type_key, null) && <CredentialEntry req={req} tech={null} existing={companyCred} onSaved={onSaved} onCancel={() => setEntry(null)} />}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Section>

        {/* ── On file ── */}
        {!needsState && !loadError && (
          <Section id="held" title="On file" sub="Everything entered so far.">
            {creds.length === 0 ? (
              <div className="cmp-empty">
                <strong>You track no licenses yet.</strong>
                <span>That’s the starting point, not a problem — enter them above and renewal warnings take it from there.</span>
              </div>
            ) : (
              <div className="cmp-table-wrap">
                <table className="cmp-table">
                  <thead><tr><th>Credential</th><th>Holder</th><th>Number</th><th>Expiry</th><th>Status</th></tr></thead>
                  <tbody>
                    {creds.map((c) => (
                      <tr key={c.id}>
                        <td className="cmp-ellip">{c.label || c.type_key}</td>
                        <td>{c.is_company ? 'Company' : (c.holder_name || `Tech #${c.holder_tech_id}`)}</td>
                        <td className="cmp-mono">{c.number || '—'}</td>
                        <td>{fmtDate(c.expiry_date)}</td>
                        <td><Pill cls={STATUS_CLASS[c.status] || 'off'}>{STATUS_LABEL[c.status] || c.status}</Pill></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        )}

        {/* ── Renewals & risk (FLAG mode — informs, never moves work) ── */}
        {!needsState && !loadError && (
          <Section id="risks" title="Renewals & risk"
            sub="Credentials lapsing within 60 days, cross-checked against booked work. Nothing on the schedule is ever changed automatically.">
            {risks.length === 0 && flags.length === 0 ? (
              <div className="cmp-muted">Nothing lapsing and no eligibility flags right now.</div>
            ) : (
              <div className="sv2-obj-list">
                {risks.map((r) => (
                  <div key={r.credential_id} className={`sv2-obj cmp-risk cmp-risk--${r.severity}`}>
                    <div className="sv2-obj-head">
                      <span className="sv2-obj-title">{r.holder_name} — {r.label}</span>
                      <Pill cls={r.severity === 'critical' ? 'danger' : r.severity === 'high' ? 'warn' : 'info'}>{r.severity}</Pill>
                    </div>
                    <div className="cmp-risk-body">
                      {expiryPhrase(r)}; <strong>{r.upcoming_jobs_next_2wk}</strong> job{r.upcoming_jobs_next_2wk === 1 ? '' : 's'} booked in the next 2 weeks
                      {r.upcoming_detail?.routes ? ` across ${r.upcoming_detail.routes} route${r.upcoming_detail.routes === 1 ? '' : 's'}` : ''}.
                      {' '}<span className="cmp-muted">Nothing on the schedule was changed — renew or reassign.</span>
                    </div>
                  </div>
                ))}
                {flags.length > 0 && (
                  <div className="sv2-obj cmp-flags">
                    <div className="sv2-obj-head"><span className="sv2-obj-title">Eligibility flags</span><Pill cls="warn">Advisory</Pill></div>
                    <div className="cmp-muted">These techs are missing a credential that gates their work. Assignment isn’t blocked — this is a heads-up.</div>
                    <ul className="cmp-flag-list">
                      {flags.map((f, i) => <li key={i}><strong>{f.name}</strong> — {f.label} <span className="cmp-muted">({f.flag})</span></li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </Section>
        )}

        {/* ── Alerts (explicit opt-in; off is the deliberate default) ── */}
        {!needsState && !loadError && (
          <Section id="alerts" title="Alerts" sub="Renewal warnings at 60, 30 and 7 days, plus on expiry. Off by default — this is an email send.">
            <div className="sv2-obj">
              <label className="cmp-toggle">
                <input type="checkbox" checked={alertsOn} disabled={busy} onChange={(e) => toggleAlerts(e.target.checked)} />
                <span className="cmp-toggle-text">
                  <strong>Email me before a license expires</strong>
                  <span className="cmp-muted">Goes to admins and the license holder.</span>
                </span>
                <Pill cls={alertsOn ? 'ok' : 'off'}>{alertsOn ? 'On' : 'Off'}</Pill>
              </label>
            </div>
          </Section>
        )}
      </div>
    </div>
  );
}
